// frontend/src/pages/PublicPricing.js
// /plans — public, transparent pricing page (cream/navy, ForBusinesses.css shell).
// Nothing here is hardcoded: every figure comes from
// GET /api/platform-settings/subscription-pricing/public, which resolves the same
// PlatformSettings the checkout uses. When the settings cannot be read we say so
// instead of printing a placeholder price.

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomerHeader from "../components/CustomerHeader";
import CustomerFooter from "../components/CustomerFooter";
import {
  FaCheckCircle,
  FaArrowRight,
  FaStoreAlt,
  FaUserFriends,
  FaChair,
  FaInfoCircle,
  FaChartLine,
  FaCalendarAlt,
  FaTags,
  FaUsers,
} from "react-icons/fa";
import "./ForBusinesses.css";
import "./PublicPricingFeatures.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const SUPPORT_EMAIL = "support@mg.ogestyle.com";

// PlatformSettings stores subscription amounts in whole dollars/cents-of-dollar
// (19.95), not in minor units — so this formats the raw value directly.
function money(amount, currency = "usd") {
  const value = Number(amount);
  if (!Number.isFinite(value)) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: String(currency || "usd").toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch (error) {
    return `$${value.toFixed(2)}`;
  }
}

function PublicPricing() {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/platform-settings/subscription-pricing/public`
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (cancelled) return;
        setPricing(data);
        setStatus("ready");
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load public pricing:", error);
        setStatus("error");
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const rate = Number(pricing?.perProfessional?.rate);
  const cap = Number(pricing?.perProfessional?.cap);
  const threshold = Number(pricing?.perProfessional?.threshold);
  const currency = pricing?.currency || "usd";
  const booth = pricing?.boothRenter || {};
  const fees = pricing?.fees || {};
  const ready = status === "ready";

  // The plans differ by ONE thing: how many professionals you are paying for. There are no feature
  // gates — nothing in the codebase restricts a feature by plan label — so every card lists the same
  // inclusions. Showing "advanced reporting" only on the dearest tier would be inventing a difference
  // the product does not have.
  const included = [
    "Every feature, on every plan",
    "Unlimited bookings & clients",
    "Each professional's own dashboard",
    "Payments, splits & payouts",
  ];

  const tiers = [
    {
      icon: <FaStoreAlt />,
      name: "One professional",
      seats: "1 professional",
      price: ready ? money(rate, currency) : "—",
      cadence: "per month, per location",
      blurb: "A solo booth, chair or studio room.",
      valueNote: null,
      points: included,
    },
    {
      icon: <FaUserFriends />,
      name: "Two professionals",
      seats: "2 professionals",
      price: ready ? money(rate * 2, currency) : "—",
      cadence: "per month, per location",
      blurb: "Two chairs sharing one location.",
      valueNote: ready
        ? `${money(rate, currency)} per professional — the same rate as a solo shop.`
        : null,
      points: included,
      featured: true,
    },
    {
      icon: <FaUsers />,
      name: "Full location",
      seats: `${threshold || "3"}+ professionals`,
      price: ready ? money(cap, currency) : "—",
      cadence: "per month, per location — flat",
      blurb: "A whole team in one place, one flat price that stops rising.",
      valueNote: ready
        ? `${money(cap / (threshold || 3), currency)} per professional at ${threshold || "3"} — less for every hire after that.`
        : null,
      points: included,
    },
  ];

  const faqs = [
    {
      q: "What counts as a professional?",
      a: "Each active professional attached to that location. It is the number of people who actually take bookings there, not the number of accounts you created.",
    },
    {
      q: "Do I pay per business or per location?",
      a: "Per location. Each location is priced on its own professional count, so a second staffed shop is simply a second location price — it never hides inside a business-wide cap.",
    },
    {
      q: "What happens when a professional leaves?",
      a: "The location's price follows the current count on the next billing cycle. Nothing is locked in and there is no penalty for shrinking.",
    },
    {
      q: "How is payment taken?",
      a: "By card through Stripe on a monthly subscription. Invoices and receipts are available in your dashboard under Settings → Invoices.",
    },
  ];

  return (
    <>
      <CustomerHeader />
      <main className="fb-page">
        <section className="fb-hero fb-hero--compact">
          <div className="fb-wrap fb-center">
            <p className="fb-eyebrow">Plans &amp; pricing</p>
            <h1 className="fb-display">Pay for the team you have</h1>
            <p className="fb-sub fb-sub--wide">
              One price per location, based on how many professionals work there.
              No setup fee, no lock-in, and no surprise upgrades as you grow.
            </p>
            <p className="pf-promise">
              Fair pricing that grows with you, not against you — the more your
              shop succeeds, the less each professional costs you.
            </p>
            <div className="fb-ctas fb-ctas--center">
              <button
                type="button"
                className="fb-btn fb-btn-primary"
                onClick={() => navigate("/register-business")}
              >
                Register your business
              </button>
              <button
                type="button"
                className="fb-btn fb-btn-outline"
                onClick={() => navigate("/features")}
              >
                See all features
              </button>
            </div>
          </div>
        </section>

        {status === "error" && (
          <section className="fb-sec">
            <div className="fb-wrap">
              <div className="pf-notice" role="status">
                <FaInfoCircle aria-hidden="true" />
                <p>
                  Pricing could not be loaded right now. Please refresh, or email{" "}
                  <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we
                  will send you the current plans.
                </p>
              </div>
            </div>
          </section>
        )}

        <section className="fb-sec">
          <div className="fb-wrap">
            <div className="fb-sec-head">
              <h2>One price per professional</h2>
              <p>
                Every plan includes the same features — the only thing that changes
                is how many professionals you are paying for. Nothing is locked
                behind a higher tier.
              </p>
            </div>
            <div className="pf-grid">
              {tiers.map((tier) => (
                <article
                  key={tier.name}
                  className={`pf-card${tier.featured ? " pf-card--featured" : ""}`}
                >
                  {tier.featured && (
                    <span className="pf-ribbon">Most locations</span>
                  )}
                  <span className="fb-ic pf-card-ic">{tier.icon}</span>
                  <h3>{tier.name}</h3>
                  <p className="pf-card-seats">{tier.seats}</p>
                  <p className="pf-price">
                    {tier.price}
                    <span className="pf-price-unit">/month</span>
                  </p>
                  <p className="pf-cadence">{tier.cadence}</p>
                  <p className="pf-blurb">{tier.blurb}</p>
                  {tier.valueNote && (
                    <p className="pf-value-note">{tier.valueNote}</p>
                  )}
                  <ul className="pf-list">
                    {tier.points.map((point) => (
                      <li key={point}>
                        <FaCheckCircle aria-hidden="true" /> {point}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    className={`fb-btn ${
                      tier.featured ? "fb-btn-primary" : "fb-btn-outline"
                    } pf-full-btn`}
                    onClick={() => navigate("/register-business")}
                  >
                    Get started <FaArrowRight aria-hidden="true" />
                  </button>
                </article>
              ))}
            </div>

            <p className="pf-footnote">
              Prices are per location, and every location is priced the same way —
              a second shop is simply a second location price, listed on your one
              monthly invoice. A location stops growing in price once it reaches{" "}
              {threshold || "3"} professionals.
            </p>
          </div>
        </section>

        <section className="fb-sec fb-sec--white">
          <div className="fb-wrap">
            <div className="fb-sec-head">
              <p className="fb-eyebrow">For your team</p>
              <h2>Every professional gets their own dashboard</h2>
              <p>
                A plan covers the whole shop, but nobody works in someone else's
                login. Each professional signs in to their own dashboard with their
                own numbers — so the team can see what is working and grow their
                own clientele.
              </p>
            </div>
            <div className="pf-dash-grid">
              <article className="pf-dash-card">
                <span className="fb-ic">
                  <FaChartLine />
                </span>
                <h3>Their own performance</h3>
                <p>
                  Services delivered, revenue generated, rebooking rate and
                  average ticket — per professional, not blended into a shop total.
                </p>
              </article>
              <article className="pf-dash-card">
                <span className="fb-ic">
                  <FaCalendarAlt />
                </span>
                <h3>Their own calendar &amp; earnings</h3>
                <p>
                  Upcoming bookings, completed visits, no-shows and a clear view of
                  what they have earned and what has been paid out.
                </p>
              </article>
              <article className="pf-dash-card">
                <span className="fb-ic">
                  <FaUserFriends />
                </span>
                <h3>Their own client book</h3>
                <p>
                  Client history, notes and preferences for the people they look
                  after, so a returning client never has to repeat themselves.
                </p>
              </article>
              <article className="pf-dash-card">
                <span className="fb-ic">
                  <FaTags />
                </span>
                <h3>Reporting that drives decisions</h3>
                <p>
                  Which services sell, which days are quiet and where the growth
                  is — reporting for the owner across the whole shop, and for each
                  professional on their own work.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className="fb-sec">
          <div className="fb-wrap">
            <div className="fb-sec-head">
              <h2>Other ways to work with OGESTYLE</h2>
            </div>
            <div className="pf-duo">
              <article className="pf-card pf-card--flat">
                <span className="fb-ic pf-card-ic">
                  <FaUserFriends />
                </span>
                <h3>Independent professional</h3>
                <p className="pf-price">
                  {ready ? money(pricing.freelancerMonthly, currency) : "—"}
                  <span className="pf-price-unit">/month</span>
                </p>
                <p className="pf-blurb">
                  Work solo without a business licence or a physical location —
                  your own profile, calendar and payments.
                </p>
                <ul className="pf-list">
                  <li>
                    <FaCheckCircle aria-hidden="true" /> Your own public booking
                    link
                  </li>
                  <li>
                    <FaCheckCircle aria-hidden="true" /> Clients, services and
                    packages
                  </li>
                  <li>
                    <FaCheckCircle aria-hidden="true" /> Payouts through Stripe
                  </li>
                </ul>
                <button
                  type="button"
                  className="fb-btn fb-btn-outline pf-full-btn"
                  onClick={() => navigate("/freelancer/signup")}
                >
                  Freelancer signup <FaArrowRight aria-hidden="true" />
                </button>
              </article>

              <article className="pf-card pf-card--flat">
                <span className="fb-ic pf-card-ic">
                  <FaChair />
                </span>
                <h3>Booth renter</h3>
                <p className="pf-price">
                  {ready
                    ? money(booth.renterSubscriptionMonthly, currency)
                    : "—"}
                  <span className="pf-price-unit">/month</span>
                </p>
                <p className="pf-blurb">
                  Rent a chair or booth inside a partner salon. Renters pay no
                  OGESTYLE subscription — you agree rent with the salon, and any
                  online rent collection is handled inside the platform.
                </p>
                <ul className="pf-list">
                  <li>
                    <FaCheckCircle aria-hidden="true" /> No platform subscription
                    for the renter
                  </li>
                  <li>
                    <FaCheckCircle aria-hidden="true" /> Your own calendar at the
                    salon
                  </li>
                  <li>
                    <FaCheckCircle aria-hidden="true" /> Rent receipts tracked for
                    you
                  </li>
                </ul>
                <button
                  type="button"
                  className="fb-btn fb-btn-outline pf-full-btn"
                  onClick={() => navigate("/for-professionals")}
                >
                  Join as a professional <FaArrowRight aria-hidden="true" />
                </button>
              </article>
            </div>

            {booth?.enabled && Number(booth.businessMonthlyFeePerRenter) > 0 && (
              <p className="pf-footnote">
                Salons that host booth renters pay{" "}
                {money(booth.businessMonthlyFeePerRenter, currency)} per active
                renter per month to cover platform and payment handling. It is
                billed on the salon's existing subscription.
              </p>
            )}
          </div>
        </section>

        <section className="fb-sec fb-sec--white">
          <div className="fb-wrap">
            <div className="fb-sec-head">
              <h2>Transaction fees, in plain sight</h2>
              <p>
                A subscription is not the whole story, so here is every other
                charge — including the ones that are zero.
              </p>
            </div>
            <div className="pf-fee-grid">
              <div className="pf-fee pf-fee--zero">
                <p className="pf-fee-value">
                  {ready ? `${fees.servicePercent}%` : "—"}
                </p>
                <p className="pf-fee-label">Platform fee on services</p>
                <p className="pf-fee-note">
                  OGESTYLE takes nothing from your services. Every dollar you
                  charge for a service stays with you and your professionals —
                  only card processing applies.
                </p>
              </div>
              <div className="pf-fee">
                <p className="pf-fee-value">
                  {ready ? `${fees.productPercent}%` : "—"}
                </p>
                <p className="pf-fee-label">Platform fee on products</p>
                <p className="pf-fee-note">
                  Charged only when a product sells through your shop. Nothing
                  is charged on services, and this is the only platform fee on a
                  product sale.
                </p>
              </div>
              <div className="pf-fee">
                <p className="pf-fee-value">
                  {ready
                    ? `${fees.stripePercent}% + ${money(
                        fees.stripeFixed,
                        currency
                      )}`
                    : "—"}
                </p>
                <p className="pf-fee-label">Card processing (Stripe)</p>
                <p className="pf-fee-note">
                  Whatever Stripe charges to move the money. It is passed through
                  at cost, not marked up.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="fb-sec">
          <div className="fb-wrap pf-faq-wrap">
            <div className="fb-sec-head">
              <h2>Questions owners actually ask</h2>
            </div>
            <div className="pf-faq">
              {faqs.map((item) => (
                <details key={item.q} className="pf-faq-item">
                  <summary>{item.q}</summary>
                  <p>{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="fb-sec fb-navy">
          <div className="fb-wrap fb-center">
            <p className="fb-eyebrow">Ready when you are</p>
            <h2>Start with the team you have today</h2>
            <p className="fb-sub fb-sub--wide">
              Add a professional when you hire one. Change or cancel from your
              dashboard without talking to anyone.
            </p>
            <div className="fb-ctas fb-ctas--center">
              <button
                type="button"
                className="fb-btn fb-btn-primary"
                onClick={() => navigate("/register-business")}
              >
                Register your business
              </button>
              <a className="fb-link fb-link--light" href={`mailto:${SUPPORT_EMAIL}`}>
                Ask a question
              </a>
            </div>
          </div>
        </section>
      </main>
      <CustomerFooter />
    </>
  );
}

export default PublicPricing;
